// pages/support/clients/ClientDetailsPage.tsx
import React, { useState, useEffect } from "react";
import PageContainer from "../../../../components/ui/PageContainer";
import PageLayout from "../../../../layouts/PageLayout";
import { Calendar, Mail, Phone, User, ArrowLeft, Plus, Search, Filter, X, Eye, Edit, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Client } from "../../../../types/features/support/Client";
import { Call } from "../../../../types/features/support/Call";
import { Ticket } from "../../../../types/features/support/Ticket";
import HorizontalTab, { TabItem } from "../../../../components/ui/HorizontalTab";
import CallList from "../../../../components/features/support/calls/CallsList";
import TicketList from "../../../../components/features/support/tickets/TicketsList";
import CallDetailsModal from "../../../../components/features/support/calls/CallDetailsModal";
import CallSaveModal from "../../../../components/features/support/calls/CallSaveModal";
import TicketSaveModal from "../../../../components/features/support/tickets/TicketSaveModal";
import TicketDetailsModal from "../../../../components/features/support/tickets/TicketDetailsModal";
import MenuModal, { Menu } from "../../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../../components/ui/DeleteConfirmationModal";
import useCalls from "../../../../hooks/support/calls/useCalls";
import useTickets from "../../../../hooks/support/tickets/useTickets";
import { alertSuccess, alertError } from "../../../../helpers/alertError";

export default function ClientDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const client: Client = location.state;

  const { calls, fetchCallsByClient, loading: callsLoading, createCall, deleteCall, updateCall } = useCalls();
  const {
    tickets,
    fetchTicketsByClient,
    loading: ticketsLoading,
    createTicket,
    updateTicket,
    deleteTicket,
  } = useTickets();

  const [activeTab, setActiveTab] = useState<string>("calls");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriority, setSelectedPriority] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);

  // États pour les appels
  const [clientCalls, setClientCalls] = useState<Call[]>([]);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [callToView, setCallToView] = useState<Call | null>(null);
  const [callToDelete, setCallToDelete] = useState<Call | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [callSaveModalOpen, setCallSaveModalOpen] = useState(false);
  const [isDeletingCall, setIsDeletingCall] = useState(false);

  // États pour les tickets
  const [clientTickets, setClientTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketToView, setTicketToView] = useState<Ticket | null>(null);
  const [ticketToEdit, setTicketToEdit] = useState<Ticket | null>(null);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketDetailsModalOpen, setTicketDetailsModalOpen] = useState(false);
  const [isDeletingTicket, setIsDeletingTicket] = useState(false);

  useEffect(() => {
    if (client?.id) {
      loadClientData();
    }
  }, [client]);

  const loadClientData = async () => {
    const callsResult = await fetchCallsByClient(client.id);
    const ticketsResult = await fetchTicketsByClient(client.id);
    setClientCalls(callsResult.data || []);
    setClientTickets(ticketsResult.data || []);
  };

  const formatDate = (date: string) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleBack = () => {
    navigate("/support/clients");
  };

  // Filtrage des appels
  const filteredCalls = clientCalls.filter((call) => {
    const matchSearch =
      searchTerm === "" ||
      call.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.client_phone?.includes(searchTerm) ||
      call.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchSearch;
  });

  // Filtrage des tickets
  const filteredTickets = clientTickets.filter((ticket) => {
    const matchSearch = searchTerm === "" || ticket.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchPriority = selectedPriority === "" || ticket.priority === selectedPriority;
    const matchStatus = selectedStatus === "" || ticket.status === selectedStatus;
    return matchSearch && matchPriority && matchStatus;
  });

  // Actions pour les appels
  const getCallActionsMenu = (call: Call): Menu[] => {
    return [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          setCallToView(call);
          setDetailsModalOpen(true);
          setSelectedCall(null);
        },
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          setCallToDelete(call);
          setSelectedCall(null);
        },
      },
    ];
  };

  // Actions pour les tickets
  const getTicketActionsMenu = (ticket: Ticket): Menu[] => {
    return [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          setTicketToView(ticket);
          setTicketDetailsModalOpen(true);
          setSelectedTicket(null);
        },
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          setTicketToEdit(ticket);
          setTicketModalOpen(true);
          setSelectedTicket(null);
        },
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          setTicketToDelete(ticket);
          setSelectedTicket(null);
        },
      },
    ];
  };

  // Handlers pour les appels
  const handleDeleteCall = async () => {
    if (!callToDelete) return;
    setIsDeletingCall(true);
    const success = await deleteCall(callToDelete.id);
    setIsDeletingCall(false);
    if (success) {
      setCallToDelete(null);
      alertSuccess("Appel supprimé avec succès");
      loadClientData();
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleCallSaved = () => {
    setCallSaveModalOpen(false);
    loadClientData();
    alertSuccess("Appel enregistré avec succès");
  };

  // Handlers pour les tickets
  const handleTicketStatusChange = async (id: string, status: Ticket["status"]) => {
    const success = await updateTicket(id, { status });
    if (success) {
      alertSuccess(
        `Ticket ${status === "resolu" ? "résolu" : status === "en_cours" ? "pris en charge" : "mis à jour"}`
      );
      setTicketDetailsModalOpen(false);
      setTicketToView(null);
      loadClientData();
    } else {
      alertError("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;
    setIsDeletingTicket(true);
    const success = await deleteTicket(ticketToDelete.id);
    setIsDeletingTicket(false);
    if (success) {
      setTicketToDelete(null);
      alertSuccess("Ticket supprimé avec succès");
      loadClientData();
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleTicketSaved = () => {
    setTicketModalOpen(false);
    alertSuccess(ticketToEdit ? "Ticket modifié" : "Ticket créé");
    setTicketToEdit(null);
    loadClientData();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedPriority("");
    setSelectedStatus("");
  };

  const hasActiveFilters = searchTerm !== "" || selectedPriority !== "" || selectedStatus !== "";

  if (!client) {
    return (
      <PageContainer>
        <PageLayout title="Détails du client">
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun client sélectionné</p>
            <button onClick={handleBack} className="mt-4 text-green-600 hover:text-green-700">
              Retour à la liste
            </button>
          </div>
        </PageLayout>
      </PageContainer>
    );
  }

  const tabs: TabItem[] = [
    {
      label: `Les appels (${clientCalls.length})`,
      value: "calls",
    },
    {
      label: `Les tickets (${clientTickets.length})`,
      value: "tickets",
    },
  ];

  const renderContent = () => {
    if (activeTab === "calls") {
      return (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un appel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500/50"
              />
            </div>
            <button
              onClick={() => setCallSaveModalOpen(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
            >
              <Plus size={18} />
              Enregistrer un appel
            </button>
          </div>

          <CallList calls={filteredCalls} onAction={(call) => setSelectedCall(call)} selectable={false} />
        </>
      );
    }

    if (activeTab === "tickets") {
      return (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un ticket..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500/50"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              >
                <Filter size={18} /> Filtrer
                {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
              </button>
              <button
                onClick={() => {
                  setTicketToEdit(null);
                  setTicketModalOpen(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
              >
                <Plus size={18} />
                Nouveau ticket
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-700">Filtres</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                  >
                    <X size={14} /> Effacer tout
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Toutes</option>
                    <option value="basse">Basse</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="haute">Haute</option>
                    <option value="critique">Critique</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Tous</option>
                    <option value="nouveau">Nouveau</option>
                    <option value="en_cours">En cours</option>
                    <option value="en_attente">En attente</option>
                    <option value="escalade">Escalade</option>
                    <option value="resolu">Résolu</option>
                    <option value="ferme">Fermé</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <TicketList tickets={filteredTickets} onAction={(ticket) => setSelectedTicket(ticket)} selectable={false} />
        </>
      );
    }

    return null;
  };

  return (
    <PageContainer>
      <PageLayout title="Détails du client">
        <div className="space-y-6">
          {/* Informations personnelles */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Informations personnelles</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <User size={14} className="text-gray-400" />
                <span className="text-gray-500">Nom complet :</span>
                <span className="text-gray-800">
                  {client.firstname} {client.lastname}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail size={14} className="text-gray-400" />
                <span className="text-gray-500">Email :</span>
                <span className="text-gray-800">{client.email || "-"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-gray-400" />
                <span className="text-gray-500">Téléphone :</span>
                <span className="text-gray-800">{client.phone || client.phone_numbers?.[0] || "-"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar size={14} className="text-gray-400" />
                <span className="text-gray-500">Inscrit depuis :</span>
                <span className="text-gray-800">{formatDate(client.created_at)}</span>
              </div>
              {client.pays && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Pays :</span>
                  <span className="text-gray-800">{client.pays}</span>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <HorizontalTab
            tabItems={tabs}
            onChange={(val) => {
              setActiveTab(val as string);
              setSearchTerm("");
              setSelectedPriority("");
              setSelectedStatus("");
              setShowFilters(false);
            }}
          />

          {/* Contenu */}
          <div className="mt-4">
            {callsLoading || ticketsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </div>
      </PageLayout>

      {/* Modals pour les appels */}
      {selectedCall && (
        <MenuModal
          title={`Appel - ${selectedCall.client.firstname || "Inconnu"}`}
          isOpen={true}
          onClose={() => setSelectedCall(null)}
          icon={null}
          menu={getCallActionsMenu(selectedCall)}
        />
      )}

      {callToView && (
        <CallDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setCallToView(null);
          }}
          call={callToView}
        />
      )}

      <CallSaveModal
        isOpen={callSaveModalOpen}
        onClose={() => setCallSaveModalOpen(false)}
        onSuccess={handleCallSaved}
      />

      <DeleteConfirmationModal
        isOpen={!!callToDelete}
        onClose={() => setCallToDelete(null)}
        onConfirm={handleDeleteCall}
        title="Supprimer l'appel"
        message={`Supprimer l'appel du ${callToDelete?.client_name || "client"} ?`}
        confirmText={isDeletingCall ? "Suppression..." : "Supprimer"}
      />

      {/* Modals pour les tickets */}
      {selectedTicket && (
        <MenuModal
          title={selectedTicket.title}
          isOpen={true}
          onClose={() => setSelectedTicket(null)}
          icon={null}
          menu={getTicketActionsMenu(selectedTicket)}
        />
      )}

      <TicketSaveModal
        isOpen={ticketModalOpen}
        onClose={() => {
          setTicketModalOpen(false);
          setTicketToEdit(null);
        }}
        onSuccess={handleTicketSaved}
        initialData={ticketToEdit || undefined}
      />

      <TicketDetailsModal
        isOpen={ticketDetailsModalOpen}
        onClose={() => {
          setTicketDetailsModalOpen(false);
          setTicketToView(null);
        }}
        ticket={ticketToView}
        onStatusChange={handleTicketStatusChange}
      />

      <DeleteConfirmationModal
        isOpen={!!ticketToDelete}
        onClose={() => setTicketToDelete(null)}
        onConfirm={handleDeleteTicket}
        title="Supprimer le ticket"
        message={`Supprimer le ticket "${ticketToDelete?.title}" ?`}
        confirmText={isDeletingTicket ? "Suppression..." : "Supprimer"}
      />
    </PageContainer>
  );
}
